import { Result } from "@bridgesplit/utils";
import { Connection, PublicKey, Transaction, VersionedTransaction } from "@solana/web3.js";

async function getContextSlot(connection: Connection) {
    const currentSlot = await connection.getSlot();
    const slots = await connection.getBlocks(currentSlot - 200);
    return slots[0];
}

export async function simulateTransaction(
    connection: Connection,
    transaction: Transaction | VersionedTransaction,
    accounts: string[],
    options?: { slot?: number }
) {
    if (isVersionedTransaction(transaction)) {
        let minContextSlot = options?.slot;
        if (minContextSlot === undefined) {
            // pass in minContextSlot to prevent mismatched slot
            minContextSlot = await getContextSlot(connection);
        }

        return await connection.simulateTransaction(transaction, {
            replaceRecentBlockhash: true,
            sigVerify: false,
            minContextSlot,
            commitment: "processed",
            accounts: { encoding: "base64", addresses: accounts }
        });
    }

    return await connection.simulateTransaction(
        transaction,
        undefined,
        accounts.map((a) => new PublicKey(a))
    );
}

export const isVersionedTransaction = (tx: Transaction | VersionedTransaction): tx is VersionedTransaction => {
    return "version" in tx;
};

export function serializeAndEncodeTransaction(transaction: Transaction | VersionedTransaction) {
    const raw = transaction.serialize({ requireAllSignatures: false });
    const txnBytes = raw instanceof Buffer ? new Uint8Array(raw) : raw;

    return Buffer.from(txnBytes).toString("base64");
}

export function trySerializeAndEncodeTransaction(transaction: Transaction | VersionedTransaction): Result<string> {
    try {
        return Result.ok(serializeAndEncodeTransaction(transaction));
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return Result.errFromMessage(`Transaction is too large to simulate as a bundle: ${message}`, {
            skipSentry: true,
            rawError: error
        });
    }
}
